import React, { useState, useEffect, useCallback } from "react";
import ChangePassword from "./ChangePassword";

const fmt = (n) =>
  n != null
    ? "₹" + parseFloat(n).toLocaleString("en-IN", { minimumFractionDigits: 2 })
    : "—";

const statusLabel = {
  both_dirs_approved: { text: "Approved", color: "#16a34a", bg: "#dcfce7" },
  payment_scheduled: { text: "Scheduled", color: "#d97706", bg: "#fef3c7" },
  paid: { text: "Paid", color: "#6b7280", bg: "#f3f4f6" },
};

export default function AccountsDashboard({ user, onLogout }) {
  const [token] = useState(localStorage.getItem("token"));
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedCase, setSelectedCase] = useState(null);
  const [modal, setModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState("both_dirs_approved");
  const [showChangePw, setShowChangePw] = useState(false);

  const [schedForm, setSchedForm] = useState({
    scheduled_date: "",
    other_deduction: "",
    other_deduction_reason: "",
  });

  const [paidForm, setPaidForm] = useState({
    paid_date: "",
    payment_mode: "neft",
    utr_cheque_no: "",
    bank_name: "",
    remarks: "",
  });

  const [preview, setPreview] = useState(null);
  const [paymentDetail, setPaymentDetail] = useState(null);

  const showToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchCases = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`/api/payments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      setCases(data.cases || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchCases(); }, [fetchCases]);

  // Live preview: GST = 18% of prize_amount, no commission
  useEffect(() => {
    if (!selectedCase) return;
    const p = parseFloat(selectedCase.prize_amount) || 0;
    const o = parseFloat(schedForm.other_deduction) || 0;
    if (p > 0) {
      const commission = +((parseFloat(selectedCase.chit_value) * 0.05)).toFixed(2);
      const gst = +(commission * 0.18).toFixed(2);
      const net = +(p - gst - o).toFixed(2);
      setPreview({ gst, net });
    } else {
      setPreview(null);
    }
  }, [selectedCase, schedForm.other_deduction]);

  // Subscriber bank passbook (server returns only this for Accounts)
  const [passbooks, setPassbooks] = useState([]);
  useEffect(() => {
    if (!selectedCase) { setPassbooks([]); return; }
    fetch(`/api/documents/${selectedCase.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setPassbooks((d.documents || []).filter(x => x.doc_type === "bank_passbook")))
      .catch(() => setPassbooks([]));
  }, [selectedCase, token]);

  const passbookBlock = (
    <div style={{ margin: "8px 0 14px", padding: "10px 12px", background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 8 }}>
      <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 6, color: "#9a3412" }}>🏦 Subscriber Bank Passbook</div>
      {passbooks.length === 0
        ? <span style={{ fontSize: 12, color: "#64748b" }}>No passbook uploaded</span>
        : passbooks.map(d => (
            <a key={d.id} href={`/api/documents/file/${d.id}?token=${token}`} target="_blank" rel="noreferrer"
               style={{ display: "inline-block", marginRight: 8, background: "#ebf8ff", color: "#2b6cb0", padding: "5px 12px", fontSize: 12, borderRadius: 6, textDecoration: "none" }}>👁 View Passbook</a>
          ))}
    </div>
  );

  const openSchedule = (c) => {
    setSelectedCase(c);
    setSchedForm({ scheduled_date: "", other_deduction: c.emp_deduction||'', other_deduction_reason: c.emp_deduction_reason||'',
      bank_account_name:'', bank_account_no:'', bank_ifsc:'', bank_name:'', bank_branch:'' });
    setModal("schedule");
  };

  const openPaid = (c) => {
    setSelectedCase(c);
    const defaultDate = c.scheduled_date
      ? new Date(c.scheduled_date).toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0];
    setPaidForm({ paid_date: defaultDate, payment_mode: "neft", utr_cheque_no: "", bank_name: "", remarks: "" });
    setModal("paid");
  };

  const openView = async (c) => {
    setSelectedCase(c);
    setPaymentDetail(null);
    setModal("view");
    try {
      const res = await fetch(`/api/payments/${c.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok) setPaymentDetail(data.payment);
    } catch {}
  };

  const submitSchedule = async () => {
    if (!schedForm.scheduled_date) return showToast("Payment date is required", "error");
    setSubmitting(true);
    try {
      const res = await fetch(`/api/payments/${selectedCase.id}/schedule`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          scheduled_date: schedForm.scheduled_date,
          bank_account_name: schedForm.bank_account_name,
          bank_account_no: schedForm.bank_account_no,
          bank_ifsc: schedForm.bank_ifsc,
          bank_name: schedForm.bank_name,
          bank_branch: schedForm.bank_branch,
          prize_amount: selectedCase.prize_amount,
          chit_value: selectedCase.chit_value,
          other_deduction: schedForm.other_deduction || 0,
          other_deduction_reason: schedForm.other_deduction_reason,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      showToast(`Payment scheduled! Net Payable: ${fmt(data.net_payable)}`);
      setModal(null);
      fetchCases();
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const submitPaid = async () => {
    if (!paidForm.paid_date || !paidForm.payment_mode || !paidForm.utr_cheque_no)
      return showToast("Date, Mode and UTR/Cheque No are required", "error");
    setSubmitting(true);
    try {
      const res = await fetch(`/api/payments/${selectedCase.id}/paid`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(paidForm),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);
      showToast("Payment marked as PAID. Case locked permanently.");
      setModal(null);
      fetchCases();
    } catch (e) {
      showToast(e.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const tabs = [
    { key: "both_dirs_approved", label: "Pending Schedule" },
    { key: "payment_scheduled", label: "Scheduled" },
    { key: "paid", label: "Paid" },
  ];
  const filtered = cases.filter((c) => c.status === activeTab);

  return (
    <div style={styles.page}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <div style={styles.headerTitle}>🏦 AH Chits — Accounts</div>
          <div style={styles.headerSub}>Payment Management</div>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.8)" }}>{user?.name || "Accounts"}</span>
          <button style={styles.refreshBtn} onClick={fetchCases}>↻ Refresh</button>
          <button style={styles.refreshBtn} onClick={() => setShowChangePw(true)}>🔒 Change Password</button>
          <button style={styles.logoutBtn} onClick={onLogout}>Logout</button>
        </div>
      </div>

      {/* Summary Cards */}
      <div style={styles.cards}>
        {tabs.map((t) => {
          const count = cases.filter((c) => c.status === t.key).length;
          const sl = statusLabel[t.key];
          return (
            <div key={t.key} style={{ ...styles.card, borderTop: `3px solid ${sl.color}`, cursor: "pointer" }}
              onClick={() => setActiveTab(t.key)}>
              <div style={{ fontSize: 28, fontWeight: 700, color: sl.color }}>{count}</div>
              <div style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>{t.label}</div>
            </div>
          );
        })}
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {tabs.map((t) => (
          <button key={t.key} style={{ ...styles.tab, ...(activeTab === t.key ? styles.tabActive : {}) }}
            onClick={() => setActiveTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {/* Table */}
      <div style={styles.tableWrap}>
        {loading ? (
          <div style={styles.center}>Loading...</div>
        ) : error ? (
          <div style={{ ...styles.center, color: "#dc2626" }}>{error}</div>
        ) : filtered.length === 0 ? (
          <div style={styles.center}>No cases in this category.</div>
        ) : (
          <table style={styles.table}>
            <thead>
              <tr>
                {["Case No.", "Group / Ticket", "Customer", "Office", "Prize Amount", activeTab === "paid" ? "Paid Date" : "Scheduled Date", "Status", "Actions"].map((h) => (
                  <th key={h} style={styles.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((c) => {
                const sl = statusLabel[c.status];
                return (
                  <tr key={c.id} style={styles.tr}>
                    <td style={{ ...styles.td, fontWeight: 600, color: "#1d4ed8" }}>{c.case_number}</td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 500 }}>{c.group_code}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>Ticket: {c.ticket_no}</div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontWeight: 500 }}>{c.customer_name}</div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>{c.customer_mobile}</div>
                    </td>
                    <td style={styles.td}>{c.office_name}</td>
                    <td style={{ ...styles.td, fontWeight: 600 }}>{fmt(c.prize_amount)}</td>
                    <td style={styles.td}>{activeTab === "paid" ? (c.paid_date ? new Date(c.paid_date).toLocaleDateString("en-IN", {timeZone:"Asia/Kolkata"}) : "—") : (c.scheduled_date ? new Date(c.scheduled_date).toLocaleDateString("en-IN", {timeZone:"Asia/Kolkata"}) : "—")}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, color: sl.color, background: sl.bg }}>{sl.text}</span>
                    </td>
                    <td style={styles.td}>
                      <div style={styles.actions}>
                        {c.status === "both_dirs_approved" && (
                          <button style={styles.btnPrimary} onClick={() => openSchedule(c)}>Schedule</button>
                        )}
                        {c.status === "payment_scheduled" && (
                          <>
                            <button style={styles.btnSuccess} onClick={() => openPaid(c)}>Mark Paid</button>
                            <button style={styles.btnOutline} onClick={() => openView(c)}>View</button>
                          </>
                        )}
                        {c.status === "paid" && (
                          <button style={styles.btnOutline} onClick={() => openView(c)}>View</button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            {/* acctTotalRow */}{activeTab !== "paid" && filtered.length > 0 && (<tr style={{ background: "#f1f5f9", borderTop: "2px solid #cbd5e1" }}><td colSpan={4} style={{ ...styles.td, fontWeight: 700, textAlign: "right" }}>Total ({filtered.length} {filtered.length === 1 ? "case" : "cases"})</td><td style={{ ...styles.td, fontWeight: 800, color: "#16a34a" }}>{fmt(filtered.reduce((sum, c) => sum + (parseFloat(c.prize_amount) || 0), 0))}</td><td colSpan={3} style={styles.td}></td></tr>)}
            </tbody>
          </table>
        )}
      </div>

      {/* Schedule Modal */}
      {modal === "schedule" && selectedCase && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitle}>Schedule Payment</div>
                <div style={styles.modalSub}>{selectedCase.case_number} — {selectedCase.customer_name}</div>
              </div>
              <button style={styles.closeBtn} onClick={() => setModal(null)}>✕</button>
            </div>

            {passbookBlock}

            {/* Case Info */}
            <div style={styles.infoBox}>
              <div style={styles.infoRow}><span>Group Code</span><strong>{selectedCase.group_code}</strong></div>
              <div style={styles.infoRow}><span>Ticket No</span><strong>{selectedCase.ticket_no}</strong></div>
              <div style={styles.infoRow}><span>Chit Value</span><strong>{fmt(selectedCase.chit_value)}</strong></div>
              <div style={styles.infoRow}><span>Bid Amount</span><strong>{fmt(selectedCase.bid_amount)}</strong></div>
              {selectedCase.emp_deduction>0&&<div style={{...styles.infoRow,color:'#92400e',background:'#fff7ed',borderRadius:'4px',padding:'4px 8px',marginTop:'4px'}}>
                <span>Employee Deduction</span><strong>₹{Number(selectedCase.emp_deduction).toLocaleString('en-IN', {timeZone:'Asia/Kolkata'})} — {selectedCase.emp_deduction_reason}</strong>
              </div>}
              <div style={{ ...styles.infoRow, borderTop: "1px solid #d1fae5", paddingTop: 8, marginTop: 4 }}>
                <span style={{ fontWeight: 600 }}>Prize Amount (Chit Value − Bid)</span>
                <strong style={{ color: "#16a34a", fontSize: 15 }}>{fmt(selectedCase.prize_amount)}</strong>
              </div>
            </div>

            <div style={styles.formGrid}>
              <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                <label style={styles.label}>Scheduled Date *</label>
                <input type="date" style={styles.input}
                  value={schedForm.scheduled_date}
                  onChange={(e) => setSchedForm({ ...schedForm, scheduled_date: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Other Deduction (₹)</label>
                <input type="number" style={styles.input} placeholder="0"
                  value={schedForm.other_deduction}
                  onChange={(e) => setSchedForm({ ...schedForm, other_deduction: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Deduction Reason</label>
                <input type="text" style={styles.input} placeholder="e.g. Loan adjustment"
                  value={schedForm.other_deduction_reason}
                  onChange={(e) => setSchedForm({ ...schedForm, other_deduction_reason: e.target.value })} />
              </div>
            </div>

            {/* Bank Details */}
            <div style={{background:'#eff6ff',border:'1px solid #bfdbfe',borderRadius:'8px',padding:'16px',marginBottom:'16px'}}>
              <div style={{fontWeight:'700',color:'#1e40af',marginBottom:'12px',fontSize:'14px'}}>🏦 Subscriber Bank Details</div>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Account Holder Name *</label>
                  <input type="text" style={styles.input} placeholder="Name as per bank account"
                    value={schedForm.bank_account_name}
                    onChange={(e) => setSchedForm({ ...schedForm, bank_account_name: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Account Number *</label>
                  <input type="text" style={styles.input} placeholder="Bank account number"
                    value={schedForm.bank_account_no}
                    onChange={(e) => setSchedForm({ ...schedForm, bank_account_no: e.target.value })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>IFSC Code *</label>
                  <input type="text" style={{...styles.input,textTransform:'uppercase'}} placeholder="e.g. SBIN0001234"
                    value={schedForm.bank_ifsc}
                    onChange={(e) => setSchedForm({ ...schedForm, bank_ifsc: e.target.value.toUpperCase() })} />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Bank Name *</label>
                  <input type="text" style={styles.input} placeholder="e.g. State Bank of India"
                    value={schedForm.bank_name}
                    onChange={(e) => setSchedForm({ ...schedForm, bank_name: e.target.value })} />
                </div>
                <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                  <label style={styles.label}>Branch</label>
                  <input type="text" style={styles.input} placeholder="Branch name"
                    value={schedForm.bank_branch}
                    onChange={(e) => setSchedForm({ ...schedForm, bank_branch: e.target.value })} />
                </div>
              </div>
            </div>

            {/* Live Preview */}
            {preview && (
              <div style={styles.previewBox}>
                <div style={styles.previewTitle}>Payment Calculation Preview</div>
                <div style={styles.previewGrid}>
                  <div style={styles.previewRow}>
                    <span>Prize Amount</span><span>{fmt(selectedCase.prize_amount)}</span>
                  </div>
                  <div style={styles.previewRow}>
                    <span>GST (18%)</span><span style={{ color: "#dc2626" }}>− {fmt(preview.gst)}</span>
                  </div>
                  {parseFloat(schedForm.other_deduction) > 0 && (
                    <div style={styles.previewRow}>
                      <span>Other Deduction</span><span style={{ color: "#dc2626" }}>− {fmt(schedForm.other_deduction)}</span>
                    </div>
                  )}
                  <div style={{ ...styles.previewRow, borderTop: "1px solid #d1d5db", paddingTop: 8, marginTop: 4, fontWeight: 700, fontSize: 15 }}>
                    <span>Net Payable</span><span style={{ color: "#16a34a" }}>{fmt(preview.net)}</span>
                  </div>
                </div>
              </div>
            )}

            <div style={styles.modalFooter}>
              <button style={styles.btnOutline} onClick={() => setModal(null)}>Cancel</button>
              <button style={styles.btnPrimary} onClick={submitSchedule} disabled={submitting}>
                {submitting ? "Saving..." : "Schedule Payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Mark Paid Modal */}
      {modal === "paid" && selectedCase && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitle}>Mark as Paid</div>
                <div style={styles.modalSub}>{selectedCase.case_number} — {selectedCase.customer_name}</div>
              </div>
              <button style={styles.closeBtn} onClick={() => setModal(null)}>✕</button>
            </div>

            <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 6, padding: "10px 14px", marginBottom: 16, fontSize: 13, color: "#92400e" }}>
              ⚠️ Once marked as paid, this case will be <strong>permanently locked</strong> and cannot be edited.
            </div>

            <div style={styles.infoBox}>
              <div style={styles.infoRow}><span>Net Payable</span><strong style={{ color: "#16a34a", fontSize: 15 }}>{fmt(selectedCase.net_payable)}</strong></div>
            </div>

            {passbookBlock}

            <div style={styles.formGrid}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Date *</label>
                <input type="date" style={styles.input}
                  value={paidForm.paid_date}
                  onChange={(e) => setPaidForm({ ...paidForm, paid_date: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Mode *</label>
                <select style={styles.input}
                  value={paidForm.payment_mode}
                  onChange={(e) => setPaidForm({ ...paidForm, payment_mode: e.target.value })}>
                  <option value="neft">NEFT</option>
                  <option value="rtgs">RTGS</option>
                  <option value="imps">IMPS</option>
                  <option value="cheque">Cheque</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>UTR / Cheque No *</label>
                <input type="text" style={styles.input} placeholder="Enter UTR or Cheque number"
                  value={paidForm.utr_cheque_no}
                  onChange={(e) => setPaidForm({ ...paidForm, utr_cheque_no: e.target.value })} />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Bank Name</label>
                <input type="text" style={styles.input} placeholder="e.g. SBI, HDFC"
                  value={paidForm.bank_name}
                  onChange={(e) => setPaidForm({ ...paidForm, bank_name: e.target.value })} />
              </div>
              <div style={{ ...styles.formGroup, gridColumn: "1 / -1" }}>
                <label style={styles.label}>Remarks</label>
                <textarea style={{ ...styles.input, height: 64, resize: "vertical" }}
                  placeholder="Optional remarks"
                  value={paidForm.remarks}
                  onChange={(e) => setPaidForm({ ...paidForm, remarks: e.target.value })} />
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button style={styles.btnOutline} onClick={() => setModal(null)}>Cancel</button>
              <button style={styles.btnSuccess} onClick={submitPaid} disabled={submitting}>
                {submitting ? "Processing..." : "Confirm Payment"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Modal */}
      {modal === "view" && selectedCase && (
        <div style={styles.overlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <div>
                <div style={styles.modalTitle}>Payment Details</div>
                <div style={styles.modalSub}>{selectedCase.case_number} — {selectedCase.customer_name}</div>
              </div>
              <button style={styles.closeBtn} onClick={() => setModal(null)}>✕</button>
            </div>

            {passbookBlock}

            {!paymentDetail ? (
              <div style={styles.center}>Loading...</div>
            ) : (
              <div>
                <div style={styles.detailSection}>
                  <div style={styles.detailSectionTitle}>Case Info</div>
                  <div style={styles.detailGrid}>
                    <DetailRow label="Group Code" value={selectedCase.group_code} />
                    <DetailRow label="Ticket No" value={selectedCase.ticket_no} />
                    <DetailRow label="Chit Value" value={fmt(selectedCase.chit_value)} />
                    <DetailRow label="Bid Amount" value={fmt(selectedCase.bid_amount)} />
                    <DetailRow label="Prize Amount" value={fmt(selectedCase.prize_amount)} highlight />
                  </div>
                </div>

                <div style={styles.detailSection}>
                  <div style={styles.detailSectionTitle}>Payment Breakdown</div>
                  <div style={styles.detailGrid}>
                    <DetailRow label="Prize Amount" value={fmt(paymentDetail.prize_amount)} />
                    <DetailRow label="GST (18%)" value={fmt(paymentDetail.gst_amount)} />
                    {paymentDetail.other_deduction > 0 && (
                      <DetailRow label="Other Deduction" value={fmt(paymentDetail.other_deduction)} />
                    )}
                    {paymentDetail.other_deduction_reason && (
                      <DetailRow label="Deduction Reason" value={paymentDetail.other_deduction_reason} />
                    )}
                    <DetailRow label="Net Payable" value={fmt(paymentDetail.net_payable)} highlight />
                  </div>
                </div>

                <div style={styles.detailSection}>
                  <div style={styles.detailSectionTitle}>Schedule Info</div>
                  <div style={styles.detailGrid}>
                    <DetailRow label="Scheduled Date" value={paymentDetail.scheduled_date ? new Date(paymentDetail.scheduled_date).toLocaleDateString("en-IN", {timeZone:"Asia/Kolkata"}) : "—"} />
                    <DetailRow label="Scheduled By" value={paymentDetail.scheduled_by_name || "—"} />
                  </div>
                </div>

                {paymentDetail.paid_date && (
                  <div style={styles.detailSection}>
                    <div style={styles.detailSectionTitle}>Payment Info</div>
                    <div style={styles.detailGrid}>
                      <DetailRow label="Paid Date" value={new Date(paymentDetail.paid_date).toLocaleDateString("en-IN", {timeZone:"Asia/Kolkata"})} />
                      <DetailRow label="Payment Mode" value={paymentDetail.payment_mode?.toUpperCase()} />
                      <DetailRow label="UTR / Cheque No" value={paymentDetail.utr_cheque_no} />
                      <DetailRow label="Bank Name" value={paymentDetail.bank_name || "—"} />
                      <DetailRow label="Updated By" value={paymentDetail.paid_by_name || "—"} />
                      {paymentDetail.remarks && <DetailRow label="Remarks" value={paymentDetail.remarks} />}
                    </div>
                  </div>
                )}
                {(paymentDetail.bank_account_no || paymentDetail.bank_account_name) && (
                  <div style={styles.detailSection}>
                    <div style={styles.detailSectionTitle}>🏦 Subscriber Bank Details</div>
                    <div style={styles.detailGrid}>
                      <DetailRow label="Account Holder" value={paymentDetail.bank_account_name || "—"} />
                      <DetailRow label="Account No" value={paymentDetail.bank_account_no || "—"} />
                      <DetailRow label="IFSC Code" value={paymentDetail.bank_ifsc || "—"} />
                      <DetailRow label="Bank Name" value={paymentDetail.subscriber_bank_name || "—"} />
                      <DetailRow label="Branch" value={paymentDetail.bank_branch || "—"} />
                    </div>
                  </div>
                )}
              </div>
            )}

            <div style={styles.modalFooter}>
              <button style={styles.btnPrimary} onClick={() => setModal(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {showChangePw && (
        <ChangePassword token={token} onClose={() => setShowChangePw(false)} />
      )}

      {/* Toast */}
      {toast && (
        <div style={{ ...styles.toast, background: toast.type === "error" ? "#dc2626" : "#16a34a" }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value, highlight }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", borderBottom: "1px solid #f3f4f6" }}>
      <span style={{ color: "#6b7280", fontSize: 13 }}>{label}</span>
      <span style={{ fontWeight: highlight ? 700 : 500, color: highlight ? "#16a34a" : "#111827", fontSize: highlight ? 15 : 13 }}>{value}</span>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: "#f9fafb", fontFamily: "'Segoe UI', sans-serif" },
  header: { background: "#1a365d", color: "#fff", padding: "0 24px", display: "flex", justifyContent: "space-between", alignItems: "center", height: 60 },
  headerTitle: { fontSize: 18, fontWeight: 700 },
  headerSub: { fontSize: 12, opacity: 0.7, marginTop: 2 },
  refreshBtn: { background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 13, color: "#fff" },
  logoutBtn: { background: "#e53e3e", border: "none", borderRadius: 6, padding: "6px 14px", cursor: "pointer", fontSize: 13, color: "#fff", fontWeight: 600 },
  cards: { display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, padding: "20px 24px 0" },
  card: { background: "#fff", borderRadius: 8, padding: "16px 20px", boxShadow: "0 1px 3px rgba(0,0,0,0.06)" },
  tabs: { display: "flex", gap: 4, margin: "16px 24px", background: "#fff", padding: 4, borderRadius: 8, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", width: "fit-content" },
  tab: { padding: "7px 18px", borderRadius: 6, border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: "#6b7280", fontWeight: 500 },
  tabActive: { background: "#1d4ed8", color: "#fff" },
  tableWrap: { background: "#fff", borderRadius: 8, boxShadow: "0 1px 3px rgba(0,0,0,0.06)", overflow: "auto", margin: "0 24px 24px" },
  table: { width: "100%", borderCollapse: "collapse" },
  th: { textAlign: "left", padding: "12px 16px", fontSize: 12, color: "#6b7280", fontWeight: 600, borderBottom: "1px solid #e5e7eb", background: "#f9fafb", textTransform: "uppercase", letterSpacing: "0.04em" },
  tr: { borderBottom: "1px solid #f3f4f6" },
  td: { padding: "13px 16px", fontSize: 13, color: "#374151", verticalAlign: "middle" },
  badge: { padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600 },
  actions: { display: "flex", gap: 6 },
  btnPrimary: { background: "#1d4ed8", color: "#fff", border: "none", borderRadius: 5, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 600 },
  btnSuccess: { background: "#16a34a", color: "#fff", border: "none", borderRadius: 5, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 600 },
  btnOutline: { background: "#fff", color: "#374151", border: "1px solid #d1d5db", borderRadius: 5, padding: "6px 14px", fontSize: 12, cursor: "pointer", fontWeight: 600 },
  center: { padding: 40, textAlign: "center", color: "#6b7280" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 16 },
  modal: { background: "#fff", borderRadius: 10, width: "100%", maxWidth: 520, maxHeight: "90vh", overflowY: "auto", padding: 24 },
  modalHeader: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 },
  modalTitle: { fontSize: 18, fontWeight: 700, color: "#111827" },
  modalSub: { fontSize: 13, color: "#6b7280", marginTop: 2 },
  closeBtn: { background: "none", border: "none", fontSize: 18, cursor: "pointer", color: "#6b7280", padding: 0, lineHeight: 1 },
  infoBox: { background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "12px 16px", marginBottom: 16 },
  infoRow: { display: "flex", justifyContent: "space-between", fontSize: 13, color: "#374151", padding: "4px 0" },
  formGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 16 },
  formGroup: { display: "flex", flexDirection: "column", gap: 5 },
  label: { fontSize: 12, fontWeight: 600, color: "#374151" },
  input: { border: "1px solid #d1d5db", borderRadius: 6, padding: "8px 10px", fontSize: 13, outline: "none", width: "100%", boxSizing: "border-box" },
  previewBox: { background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 8, padding: "14px 16px", marginBottom: 16 },
  previewTitle: { fontSize: 12, fontWeight: 700, color: "#15803d", marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.05em" },
  previewGrid: { display: "flex", flexDirection: "column", gap: 6 },
  previewRow: { display: "flex", justifyContent: "space-between", fontSize: 13, color: "#374151" },
  modalFooter: { display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, paddingTop: 16, borderTop: "1px solid #e5e7eb" },
  detailSection: { marginBottom: 20 },
  detailSectionTitle: { fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 },
  detailGrid: { display: "flex", flexDirection: "column" },
  toast: { position: "fixed", bottom: 24, right: 24, color: "#fff", padding: "12px 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, zIndex: 2000, boxShadow: "0 4px 12px rgba(0,0,0,0.2)", maxWidth: 360 },
};
